import React, { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Boat, Calendar, Info, Upload, X, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";
import { currencySymbol, formatCurrency } from "@/lib/currency";

/**
 * FreightBidderView — bidder screen for a freight auction.
 *
 * Grid: rows = shipments the creator posted, cols = { vessel, ETD, rate }.
 * Bidder can leave rows blank (cherry-pick) but at least one row must be filled
 * to submit. Backend re-validates every field.
 */
export const FreightBidderView = ({ a, onChange }) => {
  const shipments = a.shipments || [];
  const prevRow = (ref) => {
    const my = a.my_current_round_bid;
    if (!my || !my.shipment_bids) return null;
    return my.shipment_bids.find((s) => s.shipment_ref === ref) || null;
  };

  const [rows, setRows] = useState(() => {
    const init = {};
    for (const s of shipments) {
      const prev = prevRow(s.shipment_ref);
      init[s.shipment_ref] = {
        vessel_name: prev?.vessel_name || "",
        etd_committed: prev?.etd_committed || "",
        rate: prev?.rate != null ? String(prev.rate) : "",
      };
    }
    return init;
  });
  const [remarks, setRemarks] = useState(a.my_current_round_bid?.remarks || "");
  const [att, setAtt] = useState(a.my_current_round_bid?.attachment_path ? {
    storage_path: a.my_current_round_bid.attachment_path,
    original_filename: a.my_current_round_bid.attachment_filename,
  } : null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const update = (ref, patch) =>
    setRows({ ...rows, [ref]: { ...rows[ref], ...patch } });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAtt(data);
      toast.success("Attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  // A row is "filled" when at least one field has content — user intent to bid.
  const isRowFilled = (row) =>
    (row.vessel_name && row.vessel_name.trim() !== "") ||
    (row.etd_committed && row.etd_committed.trim() !== "") ||
    (row.rate && String(row.rate).trim() !== "");

  const isRowComplete = (row) =>
    row.vessel_name.trim() !== "" &&
    row.etd_committed.trim() !== "" &&
    row.rate !== "" &&
    Number(row.rate) > 0;

  // Validation summary used to enable Submit and to show per-row error hints.
  const validation = useMemo(() => {
    const errors = {};
    let filledCount = 0;
    let completeCount = 0;
    for (const s of shipments) {
      const row = rows[s.shipment_ref] || {};
      const filled = isRowFilled(row);
      if (!filled) continue;
      filledCount += 1;
      if (!row.vessel_name.trim()) errors[s.shipment_ref] = "Vessel required";
      else if (!row.etd_committed) errors[s.shipment_ref] = "ETD required";
      else if (s.cargo_ready_date && row.etd_committed < s.cargo_ready_date) errors[s.shipment_ref] = `ETD must be on/after ${s.cargo_ready_date}`;
      else if (Number(row.rate) <= 0) errors[s.shipment_ref] = "Rate must be > 0";
      else completeCount += 1;
    }
    return { errors, filledCount, completeCount };
  }, [rows, shipments]);

  const canSubmit = validation.filledCount > 0 && Object.keys(validation.errors).length === 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error("Fill at least one shipment fully (vessel + ETD + rate).");
      return;
    }
    setBusy(true);
    try {
      const shipment_bids = [];
      for (const s of shipments) {
        const row = rows[s.shipment_ref];
        if (!isRowComplete(row)) continue;
        shipment_bids.push({
          shipment_ref: s.shipment_ref,
          vessel_name: row.vessel_name.trim(),
          etd_committed: row.etd_committed,
          rate: Number(row.rate),
        });
      }
      await api.post(`/auctions/${a.id}/bids`, {
        shipment_bids,
        remarks,
        attachment_path: att?.storage_path || null,
        attachment_filename: att?.original_filename || null,
      });
      toast.success(`Bid submitted for ${shipment_bids.length} shipment${shipment_bids.length === 1 ? "" : "s"}`);
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const sym = currencySymbol(a.currency);
  const roundIx = a.current_round - 2;
  const prevTargets = roundIx >= 0 ? a.rounds?.[roundIx]?.shipment_targets : null;

  return (
    <div className="space-y-6" data-testid="freight-bidder-view">
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-[#F8F9FA] border border-black/10 p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 flex items-center gap-2">
            <Boat size={12} weight="bold" /> Container Quote · Round {a.current_round}
          </div>
          <div className="font-display font-extrabold text-3xl lg:text-4xl tracking-tightest leading-none">
            {shipments.length} shipment{shipments.length === 1 ? "" : "s"} · you quoted {validation.completeCount}
          </div>
          {a.validity_note && (
            <p className="text-xs text-neutral-600 mt-3 leading-relaxed">
              <strong>Validity:</strong> {a.validity_note}
            </p>
          )}
          <p className="text-xs text-neutral-600 mt-3 leading-relaxed">
            Cherry-pick the shipments you can lift — leave rows blank if you don&apos;t have vessel space.
            Every quote commits your vessel + ETD; the creator relies on this.
          </p>
        </div>
        <div className="lg:col-span-2 border border-black/10 p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-1">Bid Format</div>
          <div className="text-sm space-y-1.5">
            <div className="flex items-center gap-2"><Boat size={14} weight="bold" className="klein-blue" /> <span>Vessel name (assured)</span></div>
            <div className="flex items-center gap-2"><Calendar size={14} weight="bold" className="klein-blue" /> <span>Committed ETD ≥ container-pickup</span></div>
            <div className="flex items-center gap-2"><span className="font-mono-plex font-bold text-base klein-blue w-3.5 text-center">{sym}</span> <span>Rate per container</span></div>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="border border-black/10 p-6 space-y-6" data-testid="freight-bid-form">
        <div>
          <div className="border-b border-black pb-2 mb-3 flex items-baseline justify-between">
            <h3 className="font-display font-extrabold text-xl tracking-tightest">Per-Shipment Quotes</h3>
            <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
              {validation.filledCount} of {shipments.length} filled
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="freight-line-table">
              <thead>
                <tr className="border-b border-black/10 text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                  <th className="text-left p-2 w-10">#</th>
                  <th className="text-left p-2">Shipment</th>
                  <th className="text-left p-2 w-32">Container Pickup</th>
                  <th className="text-left p-2 w-32">Target ({sym})</th>
                  <th className="text-left p-2">Vessel *</th>
                  <th className="text-left p-2 w-40">Committed ETD *</th>
                  <th className="text-right p-2 w-32">Rate ({sym}) *</th>
                </tr>
              </thead>
              <tbody>
                {shipments.map((s, i) => {
                  const row = rows[s.shipment_ref] || {};
                  const err = validation.errors[s.shipment_ref];
                  const targetRate = prevTargets?.[s.shipment_ref];
                  const overTarget = targetRate != null && Number(row.rate) > targetRate;
                  return (
                    <tr key={s.shipment_ref} className={`border-b border-black/5 ${err ? "bg-red-50/40" : ""}`} data-testid={`freight-row-${s.shipment_ref}`}>
                      <td className="p-2 font-mono-plex text-neutral-500">{i + 1}</td>
                      <td className="p-2 font-mono-plex font-semibold">{s.shipment_ref}</td>
                      <td className="p-2 font-mono-plex text-xs">{s.cargo_ready_date}</td>
                      <td className="p-2 font-mono-plex text-xs">
                        {targetRate != null ? <span className="klein-blue font-semibold">{formatCurrency(targetRate, a.currency)}</span> : <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="p-2">
                        <Input
                          value={row.vessel_name}
                          onChange={(e) => update(s.shipment_ref, { vessel_name: e.target.value })}
                          placeholder="e.g. MSC OSCAR"
                          className="rounded-none border-black/20 h-9"
                          data-testid={`vessel-${s.shipment_ref}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="date"
                          min={s.cargo_ready_date}
                          value={row.etd_committed}
                          onChange={(e) => update(s.shipment_ref, { etd_committed: e.target.value })}
                          className="rounded-none border-black/20 h-9 font-mono-plex"
                          data-testid={`etd-${s.shipment_ref}`}
                        />
                      </td>
                      <td className="p-2">
                        <Input
                          type="number"
                          min="0"
                          step="any"
                          value={row.rate}
                          onChange={(e) => update(s.shipment_ref, { rate: e.target.value })}
                          placeholder="0.00"
                          className={`rounded-none h-9 text-right font-mono-plex ${overTarget ? "border-red-500 text-red-700" : "border-black/20"}`}
                          data-testid={`rate-${s.shipment_ref}`}
                        />
                        {err && (
                          <div className="text-[10px] text-red-600 mt-1" data-testid={`row-err-${s.shipment_ref}`}>{err}</div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-neutral-500 mt-3 leading-relaxed flex items-start gap-1.5">
            <Info size={12} weight="bold" className="mt-0.5 shrink-0 klein-blue" />
            <span>
              Skipping a shipment = &ldquo;no vessel space, don&apos;t award this to me&rdquo;. That&apos;s honest and preferred over bidding low without a vessel.
            </span>
          </p>
        </div>

        <div className="border-t border-black/10 pt-6">
          <div className="text-[10px] tracking-[0.25em] uppercase font-semibold mb-2">Remarks (optional)</div>
          <Textarea
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            rows={3}
            className="rounded-none border-black/20"
            data-testid="freight-remarks"
            placeholder="Anything the creator should know (e.g. rate valid until, transhipment ports, incoterms)"
          />
        </div>

        <div className="border-t border-black/10 pt-6 space-y-2">
          <div className="text-[10px] tracking-[0.25em] uppercase font-semibold">Attachment (optional)</div>
          {!att ? (
            <label className="border border-dashed border-black/20 p-3 flex items-center gap-3 cursor-pointer hover:border-black" data-testid="freight-upload-zone">
              <Upload size={16} weight="bold" />
              <span className="text-sm">{uploading ? "Uploading..." : "Click to upload (rate sheet, T&C PDF, vessel schedule…)"}</span>
              <input type="file" hidden onChange={onUpload} disabled={uploading} data-testid="freight-upload-input" />
            </label>
          ) : (
            <div className="border border-black/15 p-3 flex items-center gap-3" data-testid="freight-attachment">
              <FileText size={16} weight="bold" />
              <div className="flex-1 text-sm truncate">{att.original_filename}</div>
              <button type="button" onClick={() => setAtt(null)} data-testid="freight-attachment-remove">
                <X size={14} weight="bold" />
              </button>
            </div>
          )}
        </div>

        <Button
          type="submit"
          disabled={busy || !canSubmit}
          className="w-full rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp"
          data-testid="freight-submit-bid"
        >
          {busy ? "Submitting..." : (a.my_current_round_bid ? "Update Freight Bid" : "Submit Freight Bid")}
          <ArrowRight size={14} weight="bold" className="ml-2" />
        </Button>
        <p className="text-xs text-neutral-500 leading-relaxed">
          <strong>Tip:</strong> ties on the lowest rate go to whoever committed the earliest ETD, then whoever submitted first.
        </p>
      </form>
    </div>
  );
};
